// file: src/AdminPages/AdminLogin.jsx
import React, { useMemo, useState, useId } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { login } from "../api/adminApi";
import { useLocation } from "react-router-dom";

const BLUE = "#1271EB";
const BLUE_LIGHT = "#4D95F0";

function clsx(...parts) {
  return parts.filter(Boolean).join(" ");
}
function scorePassword(pw) {
  let s = 0;
  if (!pw) return 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}

/* Icons */
// Better Eye / EyeOff icons (rounded, crisp, 24x24)
export const Eye = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...p}
  >
    {/* Eye outline */}
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12Z" />
    {/* Iris */}
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const EyeOff = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...p}
  >
    {/* Slash */}
    <path d="M1 1l22 22" />
    {/* Left half of eye outline */}
    <path d="M1 12s4-8 11-8a11 11 0 0 1 7.31 2.69" />
    {/* Right half of eye outline */}
    <path d="M23 12s-4 8-11 8a11 11 0 0 1-7.31-2.69" />
    {/* Pupil (broken to suggest hidden) */}
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
  </svg>
);

/* Brand */
function BrandLogo({ size = 36 }) {
  return (
    <div
      className="rounded-xl shadow-lg"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(145deg, ${BLUE} 0%, ${BLUE_LIGHT} 100%)`,
        boxShadow: `0 8px 24px ${BLUE}22`,
        display: "grid",
        placeItems: "center",
      }}
      aria-hidden
    >
      <svg
        width={size * 0.6}
        height={size * 0.6}
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13"
          stroke="white"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
          fill="white"
        />
        <circle cx="7.5" cy="15.5" r="1.25" fill={BLUE} />
        <circle cx="16.5" cy="15.5" r="1.25" fill={BLUE} />
      </svg>
    </div>
  );
}

/* Toasts */
function ExpiredToast() {
  return (
    <div
      className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow"
      role="alert"
    >
      Session expired — please log in again.
    </div>
  );
}
function LoggedOutToast() {
  return (
    <div
      className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 shadow"
      role="status"
    >
      You have been logged out.
    </div>
  );
}
function ErrorToast({ message }) {
  if (!message) return null;
  return (
    <div
      className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 shadow"
      role="alert"
      aria-live="assertive"
    >
      {message}
    </div>
  );
}

/* Inputs */
function FloatingInput({
  id,
  label,
  type = "text",
  value,
  onChange,
  onBlur,
  name,
  autoComplete,
  invalid,
  error,
  rightAdornment,
}) {
  const autoId = useId();
  const inputId = id || autoId;
  return (
    <div className="relative">
      <input
        id={inputId}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        autoComplete={autoComplete}
        aria-invalid={invalid || undefined}
        className={clsx(
          "peer w-full rounded-xl border bg-white px-4 pt-6 pb-2 outline-none transition",
          "border-black/10 focus:ring-4",
          invalid
            ? "border-red-400 ring-red-500/10 focus:border-red-500"
            : "focus:border-blue-500/60 ring-[color:var(--ring)]",
        )}
        style={{ "--ring": `${BLUE}1A` }}
        placeholder=" "
      />
      <label
        htmlFor={inputId}
        className={clsx(
          "pointer-events-none absolute left-4 top-2 text-sm text-black/60 transition-all",
          "peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base peer-placeholder-shown:text-black/40",
          "peer-focus:top-2 peer-focus:text-sm",
        )}
      >
        {label}
      </label>
      {rightAdornment && (
        <div className="absolute inset-y-0 right-2 flex items-center">
          {rightAdornment}
        </div>
      )}
      {invalid && error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : null}
    </div>
  );
}
function StrengthBar({ score }) {
  return (
    <div className="mt-2 flex gap-1" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className={clsx(
            "h-1 w-full rounded",
            i <= score - 1 ? "bg-[color:var(--blue)]" : "bg-black/10",
          )}
          style={{ "--blue": BLUE }}
        />
      ))}
    </div>
  );
}

/* Validation */
const Schema = Yup.object({
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().required("Password is required"),
  remember: Yup.boolean(),
});

/* Storage helpers */
function clearAdminStorage() {
  try {
    localStorage.removeItem("access_token");
    localStorage.removeItem("token_type");
    localStorage.removeItem("admin_user");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("token_type");
    sessionStorage.removeItem("admin_user");
  } catch {}
}

export default function AdminLogin({ onSuccess }) {
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const location = useLocation();

  // Only state-based banners. URL stays /admin/login
  const reason = location.state?.reason; // 'expired' | 'logout'
  const showExpired = reason === "expired";
  const showLoggedOut = reason === "logout";

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Schema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setFormError("");
      setSubmitting(true);
      // Clear storages *before* attempting login; new session will be stored by your onSuccess.
      clearAdminStorage();
      try {
        const payload = await login({
          email: values.email,
          password: values.password,
          remember_me: true,
        });
        setSubmitting(false);
        onSuccess?.(payload); // let the caller persist fresh session
      } catch (err) {
        setSubmitting(false);
        const status = err?.response?.status;
        const msg = (
          err?.response?.data?.message ||
          err?.message ||
          ""
        ).toLowerCase();
        const isAuthError =
          status === 400 ||
          status === 401 ||
          msg.includes("invalid") ||
          msg.includes("unauthorized") ||
          msg.includes("bad credentials");
        setFormError(
          isAuthError
            ? "Invalid email or password"
            : "Something went wrong. Please try again.",
        );
      }
    },
  });

  const pwScore = useMemo(
    () => scorePassword(formik.values.password),
    [formik.values.password],
  );
  const canSubmit = formik.isValid && !submitting;

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Top bar */}
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          <BrandLogo size={32} />
          <span className="text-base font-semibold tracking-tight">
            Towing Admin Portal
          </span>
        </div>
      </div>

      {/* Grid */}
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-6 pb-12 md:grid-cols-2 md:items-center">
        {/* Left panel (design unchanged) */}
        <section className="relative hidden overflow-hidden rounded-3xl border border-black/5 bg-white p-10 md:block">
          <div
            className="pointer-events-none absolute -top-32 -left-32 h-80 w-80 rounded-full blur-3xl"
            style={{ background: `${BLUE}18` }}
          />
          <div
            className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full blur-3xl"
            style={{ background: `${BLUE_LIGHT}18` }}
          />
          <h1 className="text-4xl font-bold leading-tight">
            Towing dashboard{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: `linear-gradient(90deg, ${BLUE}, ${BLUE_LIGHT})`,
              }}
            >
              Admin
            </span>{" "}
            access
          </h1>
          <p className="mt-3 max-w-md text-black/60">
            Secure sign-in with strong validation.
          </p>
        </section>

        {/* Right auth card */}
        <section className="relative rounded-3xl border border-black/5 bg-white p-8">
          <div
            className="pointer-events-none absolute -top-16 right-6 h-24 w-24 rounded-full blur-3xl"
            style={{ background: `${BLUE}22` }}
          />
          <header>
            <h2 className="text-2xl font-semibold">Admin sign in</h2>
            <p className="mt-1 text-sm text-black/60">
              Welcome back. Enter your details.
            </p>
          </header>

          {/* Banners */}
          {showExpired && <ExpiredToast />}
          {showLoggedOut && <LoggedOutToast />}
          <ErrorToast message={formError} />

          <form
            onSubmit={formik.handleSubmit}
            className="mt-6 space-y-4"
            noValidate
          >
            <FloatingInput
              label="Email address"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              autoComplete="email"
              invalid={formik.touched.email && Boolean(formik.errors.email)}
              error={formik.errors.email}
            />

            <FloatingInput
              label="Password"
              name="password"
              type={showPw ? "text" : "password"}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              autoComplete="current-password"
              invalid={
                formik.touched.password && Boolean(formik.errors.password)
              }
              error={formik.errors.password}
              rightAdornment={
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="mr-1 rounded-lg p-2 text-black/60 hover:text-black"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              }
            />

            {/* Optional: show strength bar while typing */}
            {formik.values.password && <StrengthBar score={pwScore} />}

            <button
              type="submit"
              className={clsx(
                "group relative mt-2 inline-flex w-full items-center justify-center overflow-hidden rounded-xl px-4 py-3 font-medium text-white shadow-lg transition",
                canSubmit
                  ? "active:scale-[0.99]"
                  : "opacity-60 cursor-not-allowed",
              )}
              style={{
                backgroundImage: `linear-gradient(90deg, ${BLUE}, ${BLUE_LIGHT})`,
                boxShadow: `0 10px 24px ${BLUE}33`,
              }}
              disabled={!canSubmit}
            >
              <span
                className="absolute inset-0 translate-x-[-100%] transition-transform duration-700 group-hover:translate-x-[100%]"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, transparent, #ffffff33, transparent)",
                }}
              />
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
